import * as couponService from '@cart-services/core/couponService'
import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { getDisplayAttributes } from '@cart-services/parser/parseAttributes'
import { parserFastpassOrderItemBySkuMap } from '@cart-services/parser/parserFastpassOrderItemBySkuMap'
import { type CartItem } from '@cart-services/types'
import { CART_FORM_ERROR_MESSAGE } from '@cart-services/zod-schema/cartFormSchema'
import { CartListItem } from '@cart-ui/CartListItem/CartListItem'
import { SkeletonCartListItem } from '@cart-ui/CartListItem/SkeletonCartListItem'
import { sendFastpassAddToCartEvent, useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { CouponType } from '@hooks-api/types/dataModel/coupon'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import * as stylex from '@stylexjs/stylex'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { useMemo, useRef } from 'react'
import { Controller } from 'react-hook-form'
import { useCart } from '../CartProvider/CartProvider'

const styles = stylex.create({
  selectVariantRootStyle: {
    minWidth: {
      'default': 180,
      '@media (max-width: 768px)': 160,
    },
    width: 'fit-content',
  },
})

export interface CartListItemControllerProps {
  item: CartItem
  index: number
}

export const CartListItemController = (props: CartListItemControllerProps) => {
  const { item, index } = props
  const { campaignId, currency, projectCode, countryCode, campaignCodeFromUrl, platform } = useCart()
  const { calculateResult: campaignCouponCalculateResult } = useFpB2bCartCalculate(campaignId, campaignCodeFromUrl, platform)
  const { upsertCartItem, deleteCartItem, resetCartValues, items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { getMergedCouponCriterions } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCodeFromUrl, platform)
  const orderItemBySkuMap = useMemo(() => parserFastpassOrderItemBySkuMap(campaignCouponCalculateResult?.order_items), [campaignCouponCalculateResult])
  const { sendRemoveProductEvent } = useFastpassMerchantGoogleAnalyticEvent({ projectCode, countryCode, campaignId })
  const prevQuantityRef = useRef<Record<CartItem['cartItemId'], number>>({})

  const handleQuantityChange = useCallbackEvent((cartItemId: CartItem['cartItemId'], product: ECommerceProduct) => (value?: number) => {
    if (value === undefined || value === 0) return

    const prevQuantity = prevQuantityRef.current[cartItemId] ?? item.quantity ?? 0
    if (value < prevQuantity) {
      sendRemoveProductEvent(product)
    }
    else {
      sendFastpassAddToCartEvent({
        campaign_code: campaignCodeFromUrl || 'Empty Value',
        currency,
        value: Number(item.sale_price),
        items: [{
          item_name: item.name,
          item_brand: projectCode,
          item_id: String(item.cartItemId),
          item_variant: generateItemVariant({
            productName: item.name,
            attributes: item.attributes,
          }),
          quantity: item.quantity,
          price: Number(item.sale_price),
          currency,
        }],
      })
    }

    upsertCartItem({
      cartItemId,
      quantity: value,
    })

    prevQuantityRef.current[cartItemId] = value
  })

  /**
   * 如果刪除的是最後一個商品，則重置購物車
   */
  const handleDelete = useCallbackEvent((cartItemId: CartItem['cartItemId'], product: ECommerceProduct) => () => {
    if (cartItems.length > 1) {
      deleteCartItem({ cartItemId })
    }
    else {
      resetCartValues()
    }
    sendRemoveProductEvent(product)
  })

  const { productMap } = useFpB2bProductsInfo({ projectCode, countryCode, campaignCode: campaignCodeFromUrl, campaignId, platform })
  const product = useMemo(() => productMap?.[item.cartItemId], [productMap, item.cartItemId])
  const orderItemFromCalculate = useMemo(() => orderItemBySkuMap[item.sku], [orderItemBySkuMap, item.sku])

  const couponCriterions = useMemo(() => getMergedCouponCriterions(
    orderItemFromCalculate?.applied_coupon_ids ?? [],
    [CouponType.SPECIFIC_PRODUCT, CouponType.WAREHOUSE_PRODUCT],
  ), [getMergedCouponCriterions, orderItemFromCalculate?.applied_coupon_ids])

  const {
    stock,
    salePrice,
    productTitle,
    originalPrice,
    productPageUrl,
    maxQuantity,
  } = useMemo(() => couponService.getCartListItemProps(item, product, orderItemFromCalculate), [item, product, orderItemFromCalculate])
  const attributes = getDisplayAttributes(product)
  const productImgSrc = product?.thumbnail ?? ''

  if (!product) {
    return <SkeletonCartListItem />
  }

  return (
    <Controller
      name={`cartItems.${index}`}
      render={({ field: { ref }, fieldState: { error } }) => {
        const hasError = !!error
        const isAdjustedToMaxQuantity = error?.message === CART_FORM_ERROR_MESSAGE.ADJUST_TO_MAX_QUANTITY
        return (
          <CartListItem
            showUnitPrice
            selectVariantRootStyle={styles.selectVariantRootStyle}
            rootRef={fieldRefHelper(ref)}
            quantity={item.quantity}
            maxQuantity={maxQuantity}
            productTitle={productTitle}
            productImgSrc={productImgSrc}
            productImgWidth={60}
            originalPrice={originalPrice}
            salePrice={salePrice}
            couponCriterions={couponCriterions}
            stock={stock}
            isAddonV2={item.is_addon_v2}
            isAdjustedToMaxQuantity={isAdjustedToMaxQuantity}
            errorHighlight={hasError && !isAdjustedToMaxQuantity}
            onDelete={handleDelete(item.cartItemId, product)}
            onQuantityChange={handleQuantityChange(item.cartItemId, product)}
            currency={currency}
            attributes={attributes}
            productPageUrl={productPageUrl}
            preOrderInfo={product.pre_order_info}
          />
        )
      }}
    />
  )
}
